import { useState } from "react";
import API from "@/lib/api";
import { toast } from "sonner";

/**
 * Per-feed bulk-action handlers + their loading flags.
 *
 * Bundled in one hook because every action shares the same pattern:
 *   1. Flip a per-feed loading flag
 *   2. POST to the partner's admin endpoint
 *   3. Toast the result (totals come back from the backend so we
 *      surface them verbatim — fewer surprises for the admin)
 *   4. Refetch the catalog if the partner mutated rows
 *
 * `fetchBrands` is injected so the catalog refresh fires from inside
 * the hook (the caller never has to remember to chain it). Background
 * polling jobs (mrge deep rate refresh, mrge bg import) live in their
 * own polling loops here so the orchestrator stays free of timers.
 *
 * Returns one object per partner so the JSX consumer can spread it
 * straight into <NetworkActionsBar /> as named props.
 */
export function useNetworkActions(fetchBrands) {
  const [refreshingSkim, setRefreshingSkim] = useState(false);
  const [syncingSkimReports, setSyncingSkimReports] = useState(false);
  const [syncingSkimPayments, setSyncingSkimPayments] = useState(false);
  const [markingSkimNc, setMarkingSkimNc] = useState(false);
  const [backfillingMrge, setBackfillingMrge] = useState(false);
  const [mrgeRateRun, setMrgeRateRun] = useState(null);
  const [syncingShopnomix, setSyncingShopnomix] = useState(false);
  const [syncingShopnomixReports, setSyncingShopnomixReports] = useState(false);
  const [syncingPronomix, setSyncingPronomix] = useState(false);

  // ─── Skimlinks ────────────────────────────────────────────────
  const refreshSkimlinksRates = async () => {
    setRefreshingSkim(true);
    try {
      const { data } = await API.post("/admin/skimlinks/brands/refresh-rates");
      toast.success(
        `Skimlinks rates refreshed · ${data.updated} updated · ${data.unchanged} unchanged` +
          (data.missing ? ` · ${data.missing} not found in feed` : "")
      );
      fetchBrands();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to refresh rates");
    } finally {
      setRefreshingSkim(false);
    }
  };

  // Skimlinks §3 — aggregated reports by date (sales/clicks/EPC).
  const syncSkimlinksReports = async () => {
    setSyncingSkimReports(true);
    try {
      const { data } = await API.post("/admin/skimlinks/reports/sync", { report_by: "date" });
      const t = data.totals || {};
      toast.success(
        `Skimlinks reports synced · ${data.synced} rows` +
          (t.sales != null ? ` · ${t.sales} sales` : "") +
          (t.publisher_commission_amount ? ` · $${t.publisher_commission_amount} commission` : "")
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync Skimlinks reports");
    } finally {
      setSyncingSkimReports(false);
    }
  };

  // Skimlinks §6 — payment-status buckets (paid / scheduled / pending / unpaid).
  const syncSkimlinksPayments = async () => {
    setSyncingSkimPayments(true);
    try {
      const { data } = await API.post("/admin/skimlinks/payment-status/sync", {});
      const buckets = Object.entries(data.by_status || {})
        .map(([k, v]) => `${v} ${k}`)
        .join(" · ");
      toast.success(
        `Skimlinks payment status synced · ${data.synced} rows` +
          (buckets ? ` · ${buckets}` : "")
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync Skimlinks payment status");
    } finally {
      setSyncingSkimPayments(false);
    }
  };

  // Mark ~35% of PENDING Skimlinks actions as Non-Commission / REVERSED.
  // Dry-run first, then commit the same seed so the row set is identical.
  // Never deletes — updates status + zeros payout only.
  const markSkimlinksPendingNonCommissionable = async () => {
    if (markingSkimNc) return;
    setMarkingSkimNc(true);
    try {
      const { data: preview } = await API.post(
        "/admin/skimlinks/actions/mark-non-commissionable",
        { ratio: 0.35, commit: false },
      );
      const pending = preview.pending_total ?? 0;
      const selected = preview.selected ?? 0;
      if (!pending) {
        toast.message("No PENDING Skimlinks actions to mark");
        return;
      }
      if (!window.confirm(
        `Mark ${selected.toLocaleString()} of ${pending.toLocaleString()} PENDING Skimlinks `
        + `actions (~${preview.selected_pct}%) as Non-Commission / Rejected?\n\n`
        + "This updates status only (state → REVERSED, payout → $0). No rows are deleted.\n"
        + `Seed: ${preview.seed}`,
      )) {
        return;
      }
      const { data } = await API.post(
        "/admin/skimlinks/actions/mark-non-commissionable",
        { ratio: 0.35, commit: true, seed: preview.seed },
      );
      toast.success(
        `Skimlinks Non-Commission · updated ${data.updated ?? 0} of ${data.selected ?? 0}`
        + ` · approvals revoked ${data.approvals_revoked ?? 0}`,
      );
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to mark Skimlinks Non-Commission",
      );
    } finally {
      setMarkingSkimNc(false);
    }
  };

  // ─── mrge ─────────────────────────────────────────────────────
  // Fast catalog-level rate re-parse — no upstream API calls. Covers
  // ~16% of brands. Run BEFORE the deep refresh.
  const backfillMrgeRatesFromSummary = async () => {
    setBackfillingMrge(true);
    try {
      const { data } = await API.post("/admin/mrge/brands/backfill-rates-from-summary");
      toast.success(
        `mrge backfill · ${data.updated} updated of ${data.total} (no summary: ${data.no_summary})`
      );
      fetchBrands();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to backfill mrge rates");
    } finally {
      setBackfillingMrge(false);
    }
  };

  // Background deep rate refresh — hits per-brand /advertiser/rates
  // with concurrency 10. Polls run status every 4s; bails on terminal
  // status or a 30 min ceiling.
  const startMrgeDeepRateRefresh = async (onlyMissing) => {
    try {
      const { data } = await API.post("/admin/mrge/brands/refresh-rates/start", {
        only_missing: onlyMissing,
      });
      setMrgeRateRun({ run_id: data.run_id, status: "queued" });
      toast.success(`mrge deep rate refresh queued · run ${data.run_id.slice(0, 8)}…`);

      const startedAt = Date.now();
      const pollId = setInterval(async () => {
        try {
          const { data: r } = await API.get(`/admin/mrge/brands/refresh-rates/runs/${data.run_id}`);
          setMrgeRateRun(r);
          if (r.status === "completed") {
            clearInterval(pollId);
            const c = r.counts || {};
            toast.success(
              `mrge deep refresh done · ${c.updated || 0} updated · ${c.no_rates || 0} no rates · ${c.errors || 0} errors`
            );
            fetchBrands();
          } else if (r.status === "failed") {
            clearInterval(pollId);
            toast.error(`mrge deep refresh failed · ${r.error || "unknown"}`);
          } else if (Date.now() - startedAt > 30 * 60 * 1000) {
            clearInterval(pollId);
            toast.error("mrge deep refresh polling timed out (>30 min)");
          }
        } catch {
          // transient — keep polling
        }
      }, 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start deep refresh");
    }
  };

  // ─── CJ ───────────────────────────────────────────────────────
  const importCjBrands = async () => {
    try {
      const { data } = await API.post("/admin/cj/import-brands");
      toast.success(`Imported ${data.imported} CJ brands`);
      fetchBrands();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CJ import failed");
    }
  };

  // Shopnomix CPA catalog (non-Amazon) — manual admin sync only.
  const syncShopnomixCatalog = async () => {
    setSyncingShopnomix(true);
    try {
      const { data } = await API.post("/admin/shopnomix/sync-brands", {}, {
        timeout: 600000,
      });
      toast.success(
        `Shopnomix catalog synced · ${data.synced} brands` +
        (data.cpa_brands != null ? ` · ${data.cpa_brands} CPA` : "") +
        (data.cpc_brands != null ? ` · ${data.cpc_brands} CPC` : "") +
        ` · ${data.inserted} new · ${data.updated} updated` +
        (data.deactivated ? ` · ${data.deactivated} deactivated` : "") +
        ` · ${Math.round((data.duration_ms || 0) / 1000)}s`
      );
      fetchBrands();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Shopnomix catalog sync failed");
    } finally {
      setSyncingShopnomix(false);
    }
  };

  const syncShopnomixReports = async () => {
    setSyncingShopnomixReports(true);
    try {
      const { data } = await API.post("/admin/shopnomix/sync-report", null, {
        params: { lookback_days: 30 },
      });
      if (data.status === "queued" && data.run_id) {
        toast.loading("Shopnomix reports syncing in background…", { id: "sn-reports-sync" });
        const deadline = Date.now() + 10 * 60 * 1000;
        let final = null;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 2500));
          const { data: run } = await API.get(`/admin/shopnomix/sync-report/${data.run_id}`);
          if (run.status === "ok" || run.status === "failed") {
            final = run;
            break;
          }
        }
        if (!final) {
          toast.message("Shopnomix sync still running — check Reports shortly", { id: "sn-reports-sync" });
        } else if (final.status === "failed") {
          toast.error(final.error || "Shopnomix report sync failed", { id: "sn-reports-sync" });
        } else {
          const c = final.counts || {};
          toast.success(
            `Shopnomix reports · ${(c.rows_seen ?? 0).toLocaleString()} row(s) · `
            + `${c.upserted ?? 0} new · ${c.modified ?? 0} updated`
            + (c.auto_approved != null ? ` · ${c.auto_approved} auto-approved` : ""),
            { id: "sn-reports-sync" },
          );
        }
      } else {
        toast.success(
          `Shopnomix reports · ${(data.rows_seen ?? 0).toLocaleString()} row(s) · `
          + `${data.upserted ?? 0} new · ${data.modified ?? 0} updated`
          + (data.auto_approved != null ? ` · ${data.auto_approved} auto-approved` : ""),
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Shopnomix report sync failed");
    } finally {
      setSyncingShopnomixReports(false);
    }
  };

  const syncPronomixCatalog = async () => {
    setSyncingPronomix(true);
    try {
      const { data } = await API.post("/admin/pronomix/sync-catalog");
      toast.success(
        `Pronomix catalog synced · ${data.synced ?? 0} brands` +
        (data.inserted != null ? ` · ${data.inserted} new` : "") +
        (data.updated != null ? ` · ${data.updated} updated` : "") +
        (data.deactivated ? ` · ${data.deactivated} deactivated` : "") +
        (data.duration_ms ? ` · ${Math.round(data.duration_ms / 1000)}s` : "")
      );
      fetchBrands();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Pronomix catalog sync failed");
    } finally {
      setSyncingPronomix(false);
    }
  };

  return {
    // Skimlinks
    refreshSkimlinksRates, refreshingSkim,
    syncSkimlinksReports, syncingSkimReports,
    syncSkimlinksPayments, syncingSkimPayments,
    markSkimlinksPendingNonCommissionable, markingSkimNc,
    // mrge
    backfillMrgeRatesFromSummary, backfillingMrge,
    startMrgeDeepRateRefresh, mrgeRateRun,
    // CJ
    importCjBrands,
    // Shopnomix CPA
    syncShopnomixCatalog, syncingShopnomix,
    syncShopnomixReports, syncingShopnomixReports,
    // Pronomix CPC
    syncPronomixCatalog, syncingPronomix,
  };
}
